import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { PlusIcon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import {
  AccountTrackingReturn,
  BankAccountPurpose,
  BankAccountReturn,
  HoaAccountAssign,
} from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import {
  getUserTypeFromToken,
  getCompanyIdFromToken,
} from './utils/TokenUtils';
import { hasAdminAccess } from './constants/UserTypes';
import {
  formatCurrency,
  formatDate,
  getBankErrorParts,
} from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import { Field, FieldGroup, Fieldset, Label } from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from './common/Catalyst/dropdown';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import AccountActivityDialog from './AccountActivityDialog';

interface HoaBankAccountListProps {
  hoaId: string;
}

interface AlertState {
  title: string;
  message: string;
}

// A normalized assigned-account row so the table renders the same whether the
// data came from the live endpoint (with balances) or the metadata fallback.
interface AssignedRow {
  id: string;
  accountNumber: string;
  // The tracking API is keyed on (connection, account number) — the bank's
  // own way of naming an account — not on the assignment, so that an account
  // can be tracked before anyone assigns it and stays tracked afterwards.
  bankConnectionId: string;
  accountName?: string | null;
  purpose?: string | null;
  balance?: number | null;
  status?: string | null;
  found?: boolean;
  hasLive: boolean;
  // Undefined until the tracking state loads; null when this account has no
  // tracking row at all. The two look the same in a table and mean different
  // things, so they are kept apart.
  tracking?: AccountTrackingReturn | null;
}

const PURPOSE_OPTIONS: { value: BankAccountPurpose; label: string }[] = [
  { value: 'operating', label: 'Operating' },
  { value: 'reserve', label: 'Reserve' },
  { value: 'other', label: 'Other' },
];

/**
 * Whether we pull this account's transactions daily, and how far we have got.
 *
 * Three states worth distinguishing, because they have different remedies and
 * a single "on/off" would collapse them:
 *  - not tracked      — nobody is collecting history for this account
 *  - tracked, unsynced — on, but no run has reached it yet
 *  - tracked, synced   — with the date we last pulled
 */
const TrackingCell: React.FC<{
  row: AssignedRow;
  canManage: boolean;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
}> = ({ row, canManage, busy, onEnable, onDisable }) => {
  if (row.tracking === undefined) {
    return <span className="text-sm text-zinc-500">…</span>;
  }
  const enabled = row.tracking?.enabled ?? false;
  const synced = row.tracking?.last_synced_at ?? null;

  return (
    <div className="flex items-center gap-2">
      {enabled ? (
        <Badge color={synced ? 'lime' : 'sky'}>
          {synced ? `Synced ${formatDate(synced)}` : 'Pending first pull'}
        </Badge>
      ) : (
        <Badge color="zinc">Not tracked</Badge>
      )}
      {canManage && (
        <Button
          plain
          disabled={busy}
          onClick={enabled ? onDisable : onEnable}
          className="!text-xs"
        >
          {busy ? '…' : enabled ? 'Stop' : 'Track'}
        </Button>
      )}
    </div>
  );
};

const purposeLabel = (purpose?: string | null): string => {
  if (!purpose) return '-';
  return purpose.charAt(0).toUpperCase() + purpose.slice(1);
};

const HoaBankAccountList: React.FC<HoaBankAccountListProps> = ({ hoaId }) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const canManage = useMemo(() => hasAdminAccess(userType), [userType]);

  const [rows, setRows] = useState<AssignedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  // Set when live balances couldn't be loaded but stored assignments were.
  const [balancesNote, setBalancesNote] = useState<string | null>(null);

  // Assign dialog
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [pickerAccounts, setPickerAccounts] = useState<BankAccountReturn[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [selectedKey, setSelectedKey] = useState('');
  const [purpose, setPurpose] = useState<BankAccountPurpose>('operating');
  const [assignError, setAssignError] = useState<AlertState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Unassign dialog
  const [unassignTarget, setUnassignTarget] = useState<AssignedRow | null>(
    null
  );
  const [unassignError, setUnassignError] = useState<AlertState | null>(null);

  // Activity dialog (transactions & statements)
  const [activityRow, setActivityRow] = useState<AssignedRow | null>(null);

  // The row whose tracking toggle is in flight, and the one awaiting a
  // confirmation before being switched off.
  const [trackingBusy, setTrackingBusy] = useState<string | null>(null);
  const [disableTarget, setDisableTarget] = useState<AssignedRow | null>(null);

  const accountKey = (connectionId: string, accountNumber: string) =>
    `${connectionId}|${accountNumber}`;

  const fetchAssigned = useCallback(async () => {
    try {
      setLoading(true);
      setAlert(null);
      setBalancesNote(null);
      // Prefer the live view (balances + found status). If the bank is
      // unreachable, fall back to stored metadata so assignments stay visible.
      try {
        const live = await bankService.getHoaAccountsLive({ hoaId });
        setRows(
          live.data.accounts.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            bankConnectionId: a.bank_connection_id,
            accountName: a.account_name,
            purpose: a.purpose,
            balance: a.current_balance,
            status: a.status,
            found: a.found,
            hasLive: true,
          }))
        );
      } catch (liveErr) {
        const { status, body } = getBankErrorParts(liveErr);
        const meta = await bankService.listHoaAccounts({ hoaId });
        setRows(
          meta.data.accounts.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            bankConnectionId: a.bank_connection_id,
            accountName: a.account_name,
            purpose: a.purpose,
            status: a.last_known_status,
            hasLive: false,
          }))
        );
        setBalancesNote(
          status === 502
            ? 'Live balances are unavailable (the bank is unreachable from this environment). Showing stored assignment details.'
            : body ||
                'Live balances are unavailable right now. Showing stored assignment details.'
        );
      }
    } catch (err) {
      console.error('Error fetching HOA bank accounts:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        title: "Couldn't load bank accounts",
        message: body || 'Please try again.',
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  // Loaded after the assignments, keyed on (connection, account number).
  // Deliberately a SECOND request rather than a field on the account: an
  // account's tracking state is not a property of its assignment — it
  // outlives one, and exists for accounts that never had one.
  const fetchTracking = useCallback(async (current: AssignedRow[]) => {
    const connections = Array.from(
      new Set(current.map((r) => r.bankConnectionId).filter(Boolean))
    );
    if (connections.length === 0) return;
    try {
      const results = await Promise.all(
        connections.map((connectionId) =>
          bankService.listAccountTracking({ connectionId })
        )
      );
      const byKey = new Map<string, AccountTrackingReturn>();
      results.forEach((res) =>
        res.data.accounts.forEach((t) =>
          byKey.set(accountKey(t.bank_connection_id, t.account_number), t)
        )
      );
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          // `null` where the connection returned no row for this account:
          // it is untracked, which is different from "not loaded yet".
          tracking:
            byKey.get(accountKey(r.bankConnectionId, r.accountNumber)) ?? null,
        }))
      );
    } catch (err) {
      // Non-fatal: the assignments are the point of this table, and a
      // tracking read that fails should not blank them out.
      console.error('Error loading transaction tracking:', err);
    }
  }, []);

  const setTracking = async (row: AssignedRow, enabled: boolean) => {
    setTrackingBusy(row.id);
    setAlert(null);
    try {
      const res = await bankService.setAccountTracking({
        connectionId: row.bankConnectionId,
        accountNumber: row.accountNumber,
        accountTrackingUpdate: { enabled },
      });
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, tracking: res.data } : r))
      );
    } catch (err) {
      console.error('Error updating transaction tracking:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        title: enabled
          ? "Couldn't start tracking this account"
          : "Couldn't stop tracking this account",
        message: body || 'Please try again.',
      });
    } finally {
      setTrackingBusy(null);
    }
  };

  // Chained off `rows` rather than called inside `fetchAssigned`, so the
  // table paints as soon as the assignments arrive and the tracking column
  // fills in behind it.
  useEffect(() => {
    if (rows.length > 0 && rows.some((r) => r.tracking === undefined)) {
      void fetchTracking(rows);
    }
  }, [rows, fetchTracking]);

  useEffect(() => {
    fetchAssigned();
  }, [fetchAssigned]);

  // --- Assign ---------------------------------------------------------------

  const openAssign = async () => {
    setSelectedKey('');
    setPurpose('operating');
    setAssignError(null);
    setPickerError(null);
    setIsAssignOpen(true);

    if (!companyId) {
      setPickerError('Your account is not associated with a company.');
      return;
    }
    try {
      setPickerLoading(true);
      const response = await bankService.getCompanyBankAccounts({ companyId });
      setPickerAccounts(response.data.accounts);
      // A bank that did not answer is named rather than silently
      // missing from the list. The endpoint degrades instead of
      // failing, so without this the list is simply shorter and nothing
      // says why — which is how the wrong account gets assigned.
      const unreachable = response.data.unreachable ?? [];
      if (response.data.accounts.length === 0) {
        setPickerError(
          unreachable.length > 0
            ? `No accounts could be loaded: ${unreachable
                .map((c) => c.label || c.connection_id)
                .join(', ')} did not answer.`
            : 'No bank accounts are available to assign.'
        );
      } else if (unreachable.length > 0) {
        setPickerError(
          `This list is incomplete — ${unreachable
            .map((c) => c.label || c.connection_id)
            .join(', ')} did not answer, so its accounts are missing.`
        );
      }
    } catch (err) {
      const { status, body } = getBankErrorParts(err);
      setPickerError(
        status === 502
          ? "Couldn't load accounts from the bank (unreachable from this environment). Account assignment must be done where the bank is reachable."
          : body || "Couldn't load accounts to assign."
      );
    } finally {
      setPickerLoading(false);
    }
  };

  const closeAssign = () => {
    if (submitting) return;
    setIsAssignOpen(false);
  };

  const selectedAccount = pickerAccounts.find(
    (a) => accountKey(a.connection_id, a.account_number) === selectedKey
  );

  const handleAssign = async () => {
    if (!selectedAccount) return;
    const payload: HoaAccountAssign = {
      bank_connection_id: selectedAccount.connection_id,
      account_number: selectedAccount.account_number,
      purpose,
      account_name: selectedAccount.account_name ?? null,
      account_type: selectedAccount.account_type ?? null,
    };
    try {
      setSubmitting(true);
      setAssignError(null);
      await bankService.assignHoaAccount({
        hoaId,
        hoaAccountAssign: payload,
      });
      setIsAssignOpen(false);
      await fetchAssigned();
    } catch (err) {
      console.error('Error assigning HOA bank account:', err);
      const { status, body } = getBankErrorParts(err);
      setAssignError({
        title:
          status === 409
            ? 'Account already assigned'
            : "Couldn't assign account",
        message:
          body ||
          (status === 409
            ? 'This account is already assigned.'
            : 'Please try again.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Unassign -------------------------------------------------------------

  const handleUnassign = async () => {
    if (!unassignTarget) return;
    try {
      setSubmitting(true);
      setUnassignError(null);
      await bankService.unassignHoaAccount({ id: unassignTarget.id });
      setUnassignTarget(null);
      await fetchAssigned();
    } catch (err) {
      console.error('Error unassigning HOA bank account:', err);
      const { body } = getBankErrorParts(err);
      setUnassignError({
        title: "Couldn't unassign account",
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- Render ---------------------------------------------------------------

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4">
        <Heading level={4}>Bank Accounts</Heading>
        {canManage && (
          <Button color="sky" onClick={openAssign}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Assign Account
          </Button>
        )}
      </div>

      {alert && (
        <ShowAlert
          alertType="error"
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={8000}
          isInline={true}
        />
      )}

      {balancesNote && (
        <div className="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {balancesNote}
          </p>
        </div>
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <Text>
              No bank accounts are assigned to this HOA yet.
              {canManage ? ' Use “Assign Account” to add one.' : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Purpose
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Account #
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Name
                        </TableHeader>
                        <TableHeader className="text-base font-semibold text-right">
                          Balance
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Status
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Transactions
                        </TableHeader>
                        {canManage && (
                          <TableHeader className="text-base font-semibold">
                            <span className="sr-only">Actions</span>
                          </TableHeader>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row.id}
                          onClick={() => setActivityRow(row)}
                          className="cursor-pointer hover:bg-stone-200 dark:hover:bg-zinc-700 transition-colors duration-150"
                        >
                          <TableCell>
                            <Badge color="sky">
                              {purposeLabel(row.purpose)}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {row.accountNumber}
                          </TableCell>
                          <TableCell>{row.accountName || '-'}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {row.hasLive ? formatCurrency(row.balance) : '—'}
                          </TableCell>
                          <TableCell>
                            {row.hasLive && row.found === false ? (
                              <Badge color="amber">Not at bank</Badge>
                            ) : (
                              <Badge
                                color={
                                  row.status &&
                                  row.status.toLowerCase().includes('close')
                                    ? 'zinc'
                                    : 'lime'
                                }
                              >
                                {row.status || 'Open'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <TrackingCell
                              row={row}
                              canManage={canManage}
                              busy={trackingBusy === row.id}
                              onEnable={() => setTracking(row, true)}
                              onDisable={() => setDisableTarget(row)}
                            />
                          </TableCell>
                          {canManage && (
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Dropdown>
                                <DropdownButton plain aria-label="Actions">
                                  <EllipsisHorizontalIcon className="h-5 w-5" />
                                </DropdownButton>
                                <DropdownMenu anchor="bottom end">
                                  <DropdownItem
                                    onClick={() => {
                                      setUnassignError(null);
                                      setUnassignTarget(row);
                                    }}
                                  >
                                    Unassign
                                  </DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Assign dialog */}
      <Dialog open={isAssignOpen} onClose={closeAssign}>
        <DialogTitle>Assign Bank Account</DialogTitle>
        <DialogDescription>
          Choose one of the company's bank accounts and the role it plays for
          this HOA.
        </DialogDescription>
        <DialogBody>
          {assignError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle={assignError.title}
                alertMessage={assignError.message}
                isVisible={!!assignError}
                onClose={() => setAssignError(null)}
                timeout={12000}
                isInline={true}
              />
            </div>
          )}
          {pickerError && (
            <div className="mb-4">
              <ShowAlert
                alertType="error"
                alertTitle="Accounts unavailable"
                alertMessage={pickerError}
                isVisible={!!pickerError}
                onClose={() => setPickerError(null)}
                timeout={12000}
                isInline={true}
              />
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Account</Label>
                <FieldPicker
                  label="Account"
                  value={selectedKey}
                  disabled={pickerLoading || pickerAccounts.length === 0}
                  onChange={setSelectedKey}
                  placeholder={
                    pickerLoading ? 'Loading accounts…' : 'Select an account'
                  }
                  options={pickerAccounts.map((a) => ({
                    value: accountKey(a.connection_id, a.account_number),
                    label: `${a.account_number}${
                      a.account_name ? ` — ${a.account_name}` : ''
                    } (${a.connection_label || a.provider_key})`,
                  }))}
                />
              </Field>
              <Field>
                <Label>Purpose</Label>
                <FieldPicker
                  label="Purpose"
                  value={purpose}
                  onChange={(v) => setPurpose(v as BankAccountPurpose)}
                  options={PURPOSE_OPTIONS}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={closeAssign} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="sky"
            onClick={handleAssign}
            disabled={!selectedAccount || submitting}
          >
            {submitting ? 'Assigning…' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unassign confirmation */}
      <Dialog
        open={!!unassignTarget}
        onClose={() => !submitting && setUnassignTarget(null)}
      >
        <DialogTitle>Unassign Bank Account</DialogTitle>
        <DialogDescription>
          {unassignTarget
            ? `Remove account ${unassignTarget.accountNumber} from this HOA? `
            : 'Remove this account from this HOA? '}
          This only removes the assignment; the bank account itself is
          unaffected.
        </DialogDescription>
        <DialogBody>
          {unassignError && (
            <ShowAlert
              alertType="error"
              alertTitle={unassignError.title}
              alertMessage={unassignError.message}
              isVisible={!!unassignError}
              onClose={() => setUnassignError(null)}
              timeout={12000}
              isInline={true}
            />
          )}
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setUnassignTarget(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button color="red" onClick={handleUnassign} disabled={submitting}>
            {submitting ? 'Removing…' : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        Stopping tracking is confirmed; starting it is not. The asymmetry is
        the point: turning it on is reversible, while any gap longer than the
        bank's ~90-day retention is history that cannot be recovered from
        anywhere afterwards.
      */}
      <Dialog
        open={!!disableTarget}
        onClose={() => !trackingBusy && setDisableTarget(null)}
      >
        <DialogTitle>Stop tracking transactions?</DialogTitle>
        <DialogDescription>
          {disableTarget
            ? `Stop collecting daily transaction history for account ${disableTarget.accountNumber}?`
            : 'Stop collecting daily transaction history for this account?'}
        </DialogDescription>
        <DialogBody>
          <div className="rounded-md bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Transactions already collected are kept. But the bank only keeps
              about <strong>90 days</strong> of history — so if tracking stays
              off for longer than that, the gap can never be filled in, by us or
              by anyone else.
            </p>
          </div>
        </DialogBody>
        <DialogActions>
          <Button
            plain
            onClick={() => setDisableTarget(null)}
            disabled={!!trackingBusy}
          >
            Cancel
          </Button>
          <Button
            color="red"
            disabled={!!trackingBusy}
            onClick={async () => {
              const target = disableTarget;
              if (!target) return;
              setDisableTarget(null);
              await setTracking(target, false);
            }}
          >
            {trackingBusy ? 'Stopping…' : 'Stop tracking'}
          </Button>
        </DialogActions>
      </Dialog>

      <AccountActivityDialog
        open={!!activityRow}
        onClose={() => setActivityRow(null)}
        title={
          activityRow
            ? `${activityRow.accountNumber}${
                activityRow.accountName ? ` — ${activityRow.accountName}` : ''
              }`
            : ''
        }
        scope={
          activityRow ? { kind: 'hoa', hoaAccountId: activityRow.id } : null
        }
      />
    </>
  );
};

export default HoaBankAccountList;
